import { createContext, FC, ReactNode, useContext, useEffect, useId, useMemo } from 'react';

import { PlankSelectionItem } from '../state/types/PlankSelectionStore';

import usePlankTableStore from './usePlankTableStore';

type PlankSelectionScope = {
  /** Every selectable row of this column, in display order. Rows the virtualizer has not
   * mounted are included, so a Shift-range is never truncated to what is on screen. */
  items: PlankSelectionItem[];
  /** Position of a row within this column, or -1 when it belongs to another column. Used to
   * reject Shift-ranges that would otherwise cross from one Plank column into another. */
  indexOf: (key: string) => number;
  /** Keys of every row in this column, so a Shift-range can replace only this column's part of
   * the selection without touching rows Ctrl-selected in other columns. */
  keys: Set<string>;
};

const PlankSelectionScopeContext = createContext<PlankSelectionScope | null>(null);

/** Bounds one Plank column as a Shift-selection range scope and holds its rows' selection
 * items. Ranges resolve purely from this ordered list: no DOM is read, and rows need not be
 * mounted to take part.
 *
 * @param items Every selectable row of the column, in display order.
 */
export const PlankSelectionScopeProvider: FC<{ items?: PlankSelectionItem[]; children: ReactNode }> = ({
  items = [],
  children,
}) => {
  const scopeId = useId();
  const syncPlankSelectableKeys = usePlankTableStore((state) => state.syncPlankSelectableKeys);
  const gcPlankScope = usePlankTableStore((state) => state.gcPlankScope);

  const scope = useMemo<PlankSelectionScope>(() => {
    const indexByKey = new Map(items.map((item, index) => [item.key, index]));
    return { items, indexOf: (key) => indexByKey.get(key) ?? -1, keys: new Set(indexByKey.keys()) };
  }, [items]);

  // Drops rows that disappear on a refetch, so they cannot invisibly turn a later single drag
  // into a batch. Columns rebuild their arrays every render, so this runs often;
  // syncPlankSelectableKeys is a no-op when the keys are unchanged.
  useEffect(() => {
    syncPlankSelectableKeys(scopeId, scope.items);
  }, [scopeId, scope, syncPlankSelectableKeys]);

  useEffect(() => () => gcPlankScope(scopeId), [scopeId, gcPlankScope]);

  return <PlankSelectionScopeContext value={scope}>{children}</PlankSelectionScopeContext>;
};

/** Returns the selection scope a Plank row is rendered in, or null when it is rendered outside
 * any column (in which case Shift-range selection is not available). */
export default function usePlankSelectionScope(): PlankSelectionScope | null {
  return useContext(PlankSelectionScopeContext);
}
